import { api } from './client';
import { longRequest } from './requestTimeout';
import type { components, paths } from './generated/schema';

/**
 * WEB-2402 Д1 (решение владельца 2387-Р4, пачка 49) — строка раскладки
 * «Выручки за период» по одной валюте. Тип — из контракта сервера
 * (`schema.ts`, `RevenueCurrencyBreakdownDto`), своей копии полей нет: при
 * смене контракта кабинет не соберётся, а не разъедется молча.
 *
 * `currency: null` — деньги без кода валюты (например, проигранный спор без
 * валюты): наименьшие единицы известны, отображаемых сумм нет никогда
 * (`net`/`netArpu`/`netArppu` — null). Кабинет такую строку печатает
 * подписью «без кода валюты» без суммы — даже если число в ней пришло
 * (кэш прежнего формата), форме данных не верим.
 */
export type RevenueCurrencyBreakdown =
  components['schemas']['RevenueCurrencyBreakdownDto'];

/**
 * WEB-858 — «Выручка за период» по принятому PM определению:
 * первые оплаты + продления подписок − возвраты за тот же период.
 *
 * Приходит на строке итогов (`__totals__`) всегда, и на единственной строке
 * данных при `groupBy=none` (ревью 15.08: та строка описывает весь проект —
 * тот же объём данных, что и totals). Для настоящих группировок (country/
 * date/utm/...) продления и возвраты не несут ни квиза/пейволла, ни страны/
 * UTM, поэтому по строкам разреза их разложить нечем. Поле опционально —
 * ответ бэка без него (кэш прежнего формата) читается как «есть только
 * первые оплаты».
 *
 * WEB-2402 Д1 (пачка 49, В-13): тип берётся из контракта сервера
 * (`RevenueBreakdownDto`). При двух и более группах денег восемь чисел и
 * `currency` — null, раскладка — строками `currencies`; одно число на разные
 * деньги сервер не отдаёт. Читать числа напрямую нельзя: выбор «одно число
 * или строки по валютам» — одна функция `revenueByCurrency`
 * (`utils/revenueMetric.ts`), печать денег никогда не получает null.
 *
 * Три поля остаются необязательными сверх контракта — это ответы кэша
 * прежнего формата (до WEB-858 споров, до WEB-1611 валюты, до WEB-2402
 * раскладки): `lostDisputesCents` нет — читается как 0, `currency` нет —
 * прежний доллар, `currencies` нет — прежнее одно число.
 */
export type RevenueBreakdown = Omit<
  components['schemas']['RevenueBreakdownDto'],
  'lostDisputesCents' | 'currency' | 'currencies'
> & {
  lostDisputesCents?: number | null;
  currency?: string | null;
  currencies?: RevenueCurrencyBreakdown[];
};

/**
 * WEB-2268 — вид строки разреза (сервер: `DimensionLabelKind`,
 * `backend/src/analytics/utils/direction-label.util.ts`): что за строка, без
 * языка; фразу по виду собирает `statDimensionLabel` словарём `analytics.ts`.
 */
export type DimensionLabelKind = NonNullable<
  components['schemas']['AggregatedRowDto']['dimensionLabelKind']
>;

export interface AggregatedRow {
  dimension: string;
  dimensionLabel: string;
  /** WEB-2268: вид строки; нет — подпись как пришла. */
  dimensionLabelKind?: DimensionLabelKind;
  views: number;
  uniques: number;
  // Persistent unique-visitor count (COUNT DISTINCT visitor_id). Less affected
  // by tab refreshes than `uniques` (which is per session_id).
  uniqueVisitors: number;
  quiz_starts: number;
  quiz_start_rate: number;
  pageviews: number;
  registrations: number;
  purchases: number;
  failed_checkouts: number;
  // Revenue in whole units (e.g. dollars). Sourced from amount_cents column.
  revenue: number;
  // Revenue in minor units (cents). Use this for accurate sums.
  revenueCents: number;
  // WEB-1626: код валюты ЭТОЙ строки в нижнем регистре («usd», «jpy»), если
  // внутри строки валюта одна. Нужен таблицам, где строка — самостоятельный
  // денежный объект: строка орг-дашборда это отдельный проект, и валюта всего
  // ответа (revenueBreakdown.currency) её не описывает. Три состояния:
  // код → знак, null → валют несколько или продаж нет (печатаем без знака),
  // поля нет → ответ прежнего формата, прежний доллар (см. rowRevenueCurrency
  // в utils/revenueMetric.ts — единственная точка этого правила).
  revenueCurrency?: string | null;
  // WEB-254 B1a: new metrics ------------------------------------------------
  // Sessions where >=50% screen progress reached.
  quiz50: number;
  // Sessions where >=90% screen progress reached.
  quiz90: number;
  // PAYWALL_VIEW event count (all events, not deduped by session).
  paywall_views: number;
  // APP_INSTALLED distinct sessions.
  installs: number;
  // TRAFFBACK event count (placeholder 0 until WEB-256).
  traffback: number;
  // Выручка на ЧЕЛОВЕКА: revenue / uniqueVisitors, 0 при uniqueVisitors = 0.
  // Знаменатель — люди (persistent visitor_id), а НЕ сессии (`uniques`):
  // решение WEB-629, считает бэк в `analytics/utils/aggregated-row.util.ts`.
  // Прежняя подпись «revenue / uniques» осталась от версии до WEB-629 и врала
  // вдвое на человеке, открывшем воронку в двух вкладках — не возвращать.
  arpu: number;
  // Average Revenue Per Paying User: revenue / purchases. 0 when purchases = 0.
  arppu: number;
  // WEB-361: APP_INSTALLED / CHECKOUT_SUCCESS ratio (P10 conversion-to-app KPI).
  // null when CHECKOUT_SUCCESS = 0. Bypass-installs not counted by design.
  conversionToApp?: number | null;
  // WEB-410 BE-1: three new distinct-session metrics, mirrors backend
  // AggregatedRowDto. Optional so existing fixtures/tests built before this
  // change keep compiling without updates.
  // QUIZ_COMPLETE distinct sessions (funnel completion).
  finished?: number;
  // CHECKOUT_START distinct sessions.
  checkoutStarts?: number;
  // PAYWALL_VIEW distinct sessions (deduped session count).
  paywallUniques?: number;
  // WEB-2126: PAYWALL_HANDOFF distinct sessions — «ушли на чужую оплату».
  // Наш собственный показатель для проекта, который принимает деньги сам:
  // денежных чисел у него не будет никогда, а этот шаг — последнее, что мы
  // про человека знаем. Необязательное по той же причине, что и соседи:
  // ответы прежнего формата поля не несут (читаем как 0).
  paywallHandoffs?: number;
  // WEB-858: выручка за период (первые оплаты + продления − возвраты).
  // На строке итогов — всегда; на строке данных — только при groupBy=none
  // (ревью 15.08, см. RevenueBreakdown doc выше); у строк разреза отсутствует.
  revenueBreakdown?: RevenueBreakdown;
}

/**
 * WEB-861 — можно ли верить разнице между направлениями A/B-теста.
 *
 * Считает СЕРВЕР (`backend/src/analytics/utils/ab-significance.util.ts`):
 * сравнение двух долей z-критерием, порог 0.05. Кабинет только печатает
 * результат словами — своей копии формулы у него нет и быть не должно.
 * Приходит только при groupBy=variant и ровно одном выбранном тесте; поля
 * нет → признака нет, подпись не рисуется.
 */
export interface AbSignificance {
  /** Список вердиктов — один на два конца; сторож
   *  `components/smart-links/__tests__/abVerdicts.twoEnds.web861.guard.test.ts`
   *  сверяет его с бэковым файлом в обе стороны. */
  verdict: 'significant' | 'not-enough-data' | 'no-difference';
  comparedDimensions: string[];
  variantsConsidered: number;
  /** Лидер — ТОЛЬКО при verdict=significant, иначе null (не объявляем). */
  leaderDimension: string | null;
  leaderConversion: number;
  runnerUpDimension: string;
  runnerUpConversion: number;
  zScore: number | null;
  pValue: number | null;
  /** 1 − pValue. null — нормальное приближение неприменимо (мало данных). */
  confidence: number | null;
  requiredVisitorsPerVariant: number | null;
  remainingVisitorsPerVariant: number | null;
}

/**
 * WEB-3742 — делится ли трафик A/B-теста так, как заданы веса, и полоса
 * разброса разницы конверсий каждого направления против основы. Считает
 * СЕРВЕР (`backend/src/analytics/utils/ab-checks.util.ts`); типы — из
 * контракта, своей копии формы у кабинета нет.
 */
export type AbTrafficSplit = components['schemas']['AbTrafficSplitDto'];
export type AbDifferenceRange = components['schemas']['AbDifferenceRangeDto'];

export interface StatsResponse {
  rows: AggregatedRow[];
  totals: AggregatedRow;
  /** WEB-861 — достоверность разницы между направлениями A/B-теста. */
  abSignificance?: AbSignificance;
  /** WEB-3742 — перекос трафика; поля нет: не variant, не один тест, запрос
   *  участника или проверять нечего. */
  abTrafficSplit?: AbTrafficSplit;
  /** WEB-3742 — полосы разброса против основы; поля нет — полос нет. */
  abDifferenceRanges?: AbDifferenceRange[];
  /**
   * WEB-1812 — граница честного счёта (YYYY-MM-DD, UTC): с этой даты отметки
   * quiz50/quiz90 считаются по ФАКТИЧЕСКОМУ пути посетителя, а не по позиции
   * экрана в общем списке. Одна правда — константа бэка
   * (PATH_BASED_PROGRESS_SINCE); фронт её ТОЛЬКО печатает, своей копии не
   * держит. Поля нет → ответ прежнего формата, пометка не рисуется.
   */
  pathProgressSince?: string;
}

export interface StatsQuery {
  projectId: string;
  from?: string;
  to?: string;
  /**
   * WEB-3771 юнит 2: разрез таблицы — из контракта (`StatsQueryDto` сервера,
   * перечень STATS_GROUP_BY_VALUES), а не ручной список: новое значение
   * сервера не разойдётся с кабинетом молча.
   */
  groupBy?: paths['/analytics/stats']['get']['parameters']['query']['groupBy'];
  quizIds?: string[];
  paywallIds?: string[];
  countries?: string[];
  utmSource?: string;
  utmCampaign?: string;
  utmMedium?: string;
  // WEB-3752 юнит 2: зеркало FunnelQueryDto (WEB-3890, WEB-3891) — UTM
  // несколькими значениями и фильтр «по ответу» (`<blockId>:<valueId>`); у
  // /analytics/stats и /analytics/daily один запрос. Пришли и одиночное, и
  // список — сервер берёт список.
  utmSources?: string[];
  utmCampaigns?: string[];
  utmMediums?: string[];
  answers?: string[];
  // WEB-254 B1a: filter by member (quiz/paywall creator). Owner/admin: all members.
  memberIds?: string[];
  // WEB-259 A6: filter by specific variant (direction) IDs.
  variantIds?: string[];
  // WEB-259 A6: filter by smart_link IDs (resolves to variant_ids via direction).
  smartLinkIds?: string[];
  // WEB-503 QA follow-up: backend FunnelQueryDto already accepts domainIds
  // (multi-tenant attribution filter) — was missing from this frontend type.
  domainIds?: string[];
  // WEB-503 (Воронка filter, follow-up sub-atom): FunnelQueryDto.funnelIds,
  // applied server-side to /analytics/stats + /analytics/daily.
  funnelIds?: string[];
  // WEB-741 Фаза 2 FE: показывать Stripe test-mode оплаты в Revenue/purchases.
  // Omitted/false = дефолт (тестовые оплаты скрыты), backend FunnelQueryDto
  // coerces 'true'/'false' query string via @Transform (см. commit 4c5dc444).
  includeTestPayments?: boolean;
  /**
   * WEB-822: явное «Обновить» — считать мимо короткого кэша отчётов и
   * перезаписать его. Ставится ТОЛЬКО по кнопке, не при автозагрузке.
   */
  fresh?: boolean;
  /**
   * WEB-2404 пп.3, 9: пояс отчёта у /analytics/daily (кладёт fetchDailyThunk
   * через reportTimezone). WEB-3771 юнит 2: и у /analytics/stats — для разреза
   * «Время суток» (`groupBy=time_of_day`); при других разрезах сервер его не
   * читает. Остальные ручки статистики пояс отвергают.
   */
  tz?: string;
  /**
   * WEB-3748 мини-графики: разбивка ряда `/analytics/daily` — по дням
   * (по умолчанию) или по часам одних суток пояса `tz` (WEB-3960). Тип — из
   * контракта: новое значение на сервере не разойдётся с кабинетом молча.
   */
  bucket?: paths['/analytics/daily']['get']['parameters']['query']['bucket'];
}

/** WEB-3964: запрос и ответ итога A/B-тестов страницы — типы контракта. */
export type AbSummariesQuery =
  paths['/analytics/ab-summaries']['get']['parameters']['query'];
export type AbSummariesResponse = components['schemas']['AbSummariesResponseDto'];

export const statisticsApi = {
  stats: (query: StatsQuery) =>
    api.get<StatsResponse>('/analytics/stats', { params: query, ...longRequest('statsSummary') }),
  daily: (query: StatsQuery) =>
    api.get<StatsResponse>('/analytics/daily', { params: query, ...longRequest('statsDaily') }),
  /**
   * WEB-3964 — итог A/B-тестов страницы списка одним запросом (до 20 id): по
   * строке на каждый id без повторов, в порядке запроса; `abSignificance` —
   * тот же объект, что у раскрытия (`stats` с groupBy=variant и одним
   * тестом), null — сравнивать нечего. Период — тот же, что у раскрытия.
   */
  abSummaries: (query: AbSummariesQuery) =>
    api.get<AbSummariesResponse>('/analytics/ab-summaries', {
      params: query,
      ...longRequest('abSummaries'),
    }),
};
